import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, combineLatest, filter, forkJoin, Observable, take } from 'rxjs';
import { Subscription } from 'rxjs';
import {
  deleteFKFailure,
  deleteFKSuccess,
  deleteForeignKey,
  getLookUpTables,
  updateFKFailure,
  updateForeignKey
} from 'src/app/STORE/actions/datamodel.actions';
import { getIndices, setIndices } from 'src/app/STORE/actions/indexdesigner.actions';
import {
  allTables,
  fKSaveFailure,
  fkDelFailure,
  fkDelSuccess,
  lookUpTables
} from 'src/app/STORE/selectors/datamodel.selector';
import { allIndices, indices } from 'src/app/STORE/selectors/indexdesigner.selector';
import { tableFieldsForDM } from 'src/app/STORE/selectors/services.selector';
import { ApiConstants } from 'src/app/base/api-constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { UtilService } from 'src/app/services/util.service';
import { v4 as uuidv4 } from 'uuid';
import { IndexType } from '../index-designer/index-designer.component';
import { ReferenceUtilService } from 'src/app/components/reference-tree-view/reference-util.service';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { ApplicationTypes } from 'src/app/services/util.enum';
import { GlobalConstants } from 'src/app/base/global.constants';
import { AutoComplete } from 'primeng/autocomplete';
import { metaInfo } from 'src/app/STORE/selectors/applifecycle.selector';
import { FsBaseService } from 'src/app/services/fs-base.service';
import { ActionType, ModelType, UiActions } from '../reference-tree-view/reference.enum';

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-vs-foreign-key-tbl',
  templateUrl: './vs-foreign-key-tbl.component.html',
  styleUrls: ['./vs-foreign-key-tbl.component.scss'],
  providers: [DialogService],
  standalone: false //NOSONAR
})
export class VsForeignKeyTblComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private messageService = inject(MessageService);
  private appGlobalService = inject(AppGlobalService);
  private bs = inject(HttpBaseService);
  private loader = inject(AppLoaderService);
  private translate = inject(TranslateService);
  private zone = inject(NgZone);
  private util = inject(UtilService);
  private referenceUtil = inject(ReferenceUtilService);
  private fsService = inject(FsBaseService);

  @ViewChild('autoComplete') autoComplete!: AutoComplete;
  @Input() formData: any;
  @Input() fieldIdMap: any;
  @Input() tableIdMap: any;
  @Input() disableField = false;
  @Input() pickList;
  @Input() applicationType = null;
  foreignKeys: any[] = [];
  allForeignKeys: any[] = []; // Includes deleted FKs for name generation
  backupForeignKey: any;
  mappings: any[] = [];
  svgPaths = GlobalConstants.svgPaths;
  oldMappings: any[] = [];
  tableName: any;
  allFields: any[] = [];
  tableMap: {};
  fieldMap: {};
  currentTblindices: any = [];
  foreignKeyForm: any = new FormGroup({
    sid: new FormControl(''),
    fkName: new FormControl('', [Validators.required, Validators.pattern('^[a-zA-Z]+[a-zA-Z0-9_]*[a-zA-Z0-9]*$')]),
    parentTableSid: new FormControl('', [Validators.required]),
    parentIndexId: new FormControl('', [Validators.required])
  });
  showAddForm = false;
  groupedLookUpTables: any[] = [];
  errorFlag = false;
  message = '';
  tableList: any[] = [];
  indexList: any[] = [];
  childtableFields: any[] = [];
  childtableFlds: any[] = [];
  parentTblFlds: any[] = [];
  action = 'Save';
  headerLabel = 'ADD_FOREIGN_KEY';
  isLoading = false;
  subscriptions: Subscription[] = [];
  indexData: any = null;
  submitted = false;
  indexNameMap: any = null;
  hasReference = false;
  results: any = [];
  metaInfo: any;
  refMetaInfo: any;
  appId: any;
  orgId: any;
  fsDataSub: any;
  initialCall: boolean = true;

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  addToSubscriptionsList(subName) {
    this.subscriptions.push(subName);
  }

  private allFields$ = new BehaviorSubject<any[]>([]);
  private parentTblFields$ = new BehaviorSubject<any[]>([]);

  private getReferredAppId(tableId: any) {
    let referredAppId = null;
    if (this.applicationType != ApplicationTypes.INDEPENDENT) {
      const referencedTableApp = this.groupedLookUpTables.find(
        (obj: any) => Array.isArray(obj.items) && obj.items.some((item: any) => item.sid === tableId)
      );
      if (referencedTableApp?.sid) {
        referredAppId = referencedTableApp.sid;
      }
    }
    return referredAppId;
  }

  set(value: any, isUpdate: boolean = false) {
    let data = cloneDeep(value);
    delete data.parentIndexName;
    data = {
      ...data,
      tableId: this.formData?.sid,
      appId: this.appId,
      orgId: this.orgId
    };
    let foreignKeyData = JSON.stringify(data);
    this.store.dispatch(updateFKFailure({ fKSaveFailure: null }));
    let updfkapiConstants = isUpdate ? { ...ApiConstants.updateForeignKey } : { ...ApiConstants.saveForeignKey };
    this.store.dispatch(
      updateForeignKey({
        updfkapiConstants,
        foreignKeyData
      })
    );
  }

  setValue(value) {
    try {
      const val = JSON.parse(value);
      this.foreignKeys = val;
    } catch (e) {
      console.error(e);
    }
  }

  addForeignKey() {
    this.action = 'Save';
    this.tableName = this.formData?.name;
    this.showAddForm = true;
    this.foreignKeyForm.reset();
    this.headerLabel = 'ADD_FOREIGN_KEY';
  }

  /**
   * Resolves index name from metaInfo or refMetaInfo
   */
  getIndexName(indexId: string, fallbackName?: string): string {
    if (!indexId) return fallbackName || '';
    return this.metaInfo?.[indexId] || this.refMetaInfo?.[indexId] || fallbackName || '';
  }

  getIndices(sid: any, indId?: any, selectedAppId?: any) {
    this.isLoading = true;
    const getApiConstants = ApiConstants.getIndex;
    this.store.dispatch(setIndices({ indices: null }));
    const referredAppId = this.getReferredAppId(sid);
    const data = { tableId: sid, appId: selectedAppId, orgId: this.orgId, referredAppId: referredAppId };
    this.store.dispatch(
      getIndices({
        getApiConstants,
        data
      })
    );
    let fldSub = this.store.select(indices).subscribe((res: any) => {
      this.isLoading = false;
      if (res) {
        try {
          this.indexList = res.filter(
            (ind) => ind.indexType === IndexType.PRIMARY || ind.indexType === IndexType.UNIQUE
          );
          if (indId) {
            this.indexData = this.indexList.find((ind) => ind.sid === indId);
          }
        } catch (e) {
          console.error(e);
        }
      }
    });
    this.addToSubscriptionsList(fldSub);
  }

  editForeignKey(data) {
    this.action = 'Update';
    this.headerLabel = 'UPDATE_FOREIGN_KEY';
    this.backupForeignKey = data;
    // this.getParentTableFields(data);
    this.onTblChange({ value: data.parentTableSid }, data.parentIndexId, data);

    if (this.applicationType !== ApplicationTypes.INDEPENDENT) {
      this.groupedLookUpTables.filter(
        (app) => app.items.filter((tbl) => tbl.sid === data.parentTableSid).length > 0
      )[0].selected = true;
    }
    // this.getIndices(data.parentTableSid, data.parentIndexId);
    this.mappings = cloneDeep(data.mappings).map((dt) => {
      return {
        ...dt,
        ...{ child: dt.child ? { sid: dt.child, name: this.fieldIdMap[dt.child] } : '' }
      };
    });

    this.oldMappings = cloneDeep(this.mappings);
    this.tableName = this.formData?.name;
    this.showAddForm = true;
    this.foreignKeyForm.patchValue(data);
    combineLatest([this.allFields$, this.parentTblFields$])
      .pipe(
        filter(([allFields, parentTblFields]) => allFields?.length > 0 && parentTblFields?.length > 0),
        take(1)
      )
      .subscribe(() => {
        this.validateForeignKey({ ...data, mappings: this.mappings });
      });
  }
  parentFields: any[] = [];
  childFields: any[] = [];

  validateForeignKey(data?: any) {
    this.childFields = this.allFields;
    this.parentFields = this.parentTblFlds;
    data.mappings.forEach((map: any) => {
      const parentField = this.parentFields.find((f) => f.sid === map.parent);
      const childField = this.childFields.find((f) => f.sid === map.child.sid);
      let msg = '';
      let id: string[] = [];
      if (!map.child || !map.child.sid) {
        map.errorType = 'DELETED_FIELD';
        map.errorMsg = this.translate.instant('CHILD_FIELD_NULL');
      } else if (!childField) {
        id.push(map.child);
        this.util.handleDeletedIds(id, false, '', false, 'childfieldDelete', (finalMessage) => {
          map.errorMsg = finalMessage;
        });
        map.errorType = 'DELETED_FIELD';
      } else if (parentField && childField && parentField.f_type !== childField.f_type) {
        msg = this.translate.instant('TYPE_MISMATCH_IN_FK', {
          parentField: parentField.name,
          parentType: parentField.f_type,
          childField: childField.name,
          childType: childField.f_type
        });
        map.errorMsg = msg;
        map.errorType = 'TYPE_MISMATCH';
      }
      this.cdr.detectChanges();
    });
  }

  hasError(data: any): boolean {
    return data?.errorType === 'DELETED_FIELD';
  }

  hasWarning(data: any): boolean {
    return data?.errorType === 'TYPE_MISMATCH';
  }

  showWarningPopUp: boolean = false;

  toggleWarningPopup(data: any, state: boolean) {
    data.showPopup = state;
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  onTblChange(event, indId?: string, foreignKeyData?: any) {
    const tableId = event.value;
    if (this.applicationType !== ApplicationTypes.INDEPENDENT) {
      this.groupedLookUpTables.filter((app) => app.items.filter((tbl) => tbl.sid === tableId).length > 0)[0].selected =
        true;
    }
    let tableName, selectedAppId;
    if (this.applicationType == 'INDEPENDENT') {
      tableName = this.tableList.find((tb) => tb.sid === tableId)?.name;
      selectedAppId = this.appId;
    } else {
      const selectedApp = this.groupedLookUpTables.find((app) => app.items.some((tbl) => tbl.sid == tableId));
      const selectedTable = selectedApp?.items.find((tb) => tb.sid == tableId);
      tableName = selectedTable?.name;
      selectedAppId = selectedApp?.sid;
    }
    let i = this.allForeignKeys.filter((key) => key.parentTableSid === tableId).length + 1;
    tableName = tableName.trim().replaceAll(' ', '_').substring(0, 25);
    const currentTableName = this.tableIdMap[this.formData.sid]?.trim().replaceAll(' ', '_').substring(0, 25);
    if (!(i >= 1) || !i) {
      i = 1;
    }
    if (tableName) {
      tableName = tableName.trim().replace(/\s\s+/g, ' ').replace(/ /g, '_');
      this.foreignKeyForm.get('fkName').setValue(`${tableName}_${currentTableName}_ref${i}`);
    }
    this.foreignKeyForm.get('parentIndexId').setValue(null);
    this.parentTblFlds = [];
    this.mappings = [];
    this.getIndices(tableId, indId, selectedAppId);
    const referredAppId = this.getReferredAppId(tableId);
    const params = { tableId: tableId };
    if (referredAppId) {
      params['referredAppId'] = referredAppId;
    }
    const fieldInfoSub = this.bs.get(ApiConstants.getFieldInfo, params).subscribe({
      next: (data: any) => {
        try {
          if (data) {
            this.parentTblFlds = data;
            this.parentTblFields$.next(this.parentTblFlds);
            // this.validateForeignKey(foreignKeyData);
            if (this.applicationType !== 'INDEPENDENT') {
              data.forEach((dt) => {
                this.fieldMap[dt.sid] = dt.name;
              });
            }
          }
        } catch (e) {
          console.error(e);
        }
      },
      error: (error: any) => {
        console.error(error);
      }
    });
    this.addToSubscriptionsList(fieldInfoSub);
  }

  onIndChange(event) {
    const indid = event.value;
    const index = this.indexList.find((ind) => ind.sid === indid);
    if (index) {
      this.indexData = index;
      if (index.fields?.length) {
        this.mappings = index.fields.map((fld) => {
          return {
            parent: fld,
            child: null
          };
        });
        return;
      }
    }
    this.mappings = [];
  }

  search(event, data) {
    const parentField = this.parentTblFlds.find((f) => f.sid === data.parent);
    if (!parentField) {
      this.results = [];
      return;
    }
    const lookupToDataTypeArray = GlobalConstants.dataTypeForeignkeyMap[parentField.f_type?.toLowerCase()];
    const filteredFields = this.childtableFields
      .filter((dt) => {
        const fieldType = dt.f_type?.toLowerCase();
        return (
          fieldType !== 'radio' &&
          fieldType !== 'dropdown' &&
          lookupToDataTypeArray?.map((type_var) => type_var.toLowerCase()).includes(fieldType) &&
          dt.name.toLowerCase().includes(event.query.toLowerCase()) &&
          dt.sid !== data.parent
        );
      });

    const seen = new Set();
    this.results = filteredFields
      .filter((fld) => {
        if (!fld?.sid || seen.has(fld.sid)) {
          return false;
        }
        seen.add(fld.sid);
        return true;
      })
      .map((fld) => ({ sid: fld.sid, name: fld.name, isFkey: fld?.f_hasFK }));
  }

  isDuplicateName(value) {
    if (
      this.foreignKeys.find((key) => key.fkName.toLowerCase() === value.fkName.toLowerCase() && key.sid !== value.sid)
    ) {
      return true;
    }
    return false;
  }

  validateMappings() {
    const messages: any = [];
    const items = this.mappings.map((map) => {
      if (map.error && !map?.error.toLowerCase().includes('fill')) {
        messages.push(map.error);
      } else if (!map.child) {
        map.error = `Fill the Child Field of ${this.fieldIdMap[map.parent]}`;
        messages.push(map.error);
      } else {
        delete map.error;
      }
      return map;
    });
    this.mappings = [...items];
    if (messages.length > 0) {
      this.message = messages?.join(',');
    } else {
      const res = this.mappings.map((map) => {
        return map;
      });
      this.mappings = [...res];
    }
    return true;
  }
  trimName(name: any) {
    const title = name.trim().replace(/\s\s+/g, ' ');
    const fieldName = title.toLowerCase();
    return fieldName;
  }

  onUpdate() {
    this.message = '';
    this.errorFlag = false;
    this.submitted = true;

    const value = { ...this.foreignKeyForm.value };
    const mappings = this.buildMappingsArray();
    const validationMessages = this.collectValidationMessages(value, mappings);

    if (validationMessages.length > 0) {
      this.message = validationMessages.join(',\n');
      this.showMessage('error', this.message);
      this.errorFlag = true;
      return;
    }

    this.errorFlag = false;

    if (!this.foreignKeyForm.valid) {
      return;
    }

    this.messageService.clear();
    const data = this.buildForeignKeyData(mappings);

    if (this.action === 'Save') {
      this.executeSaveAction(data);
    } else if (this.action === 'Update') {
      this.executeUpdateAction(data);
    }
  }

  private buildMappingsArray(): { parent: string; child: string }[] {
    return this.mappings.map((dt) => ({
      parent: dt.parent,
      child: dt?.child?.sid ? dt.child.sid : dt.child
    }));
  }

  private collectValidationMessages(value: any, mappings: { parent: string; child: string }[]): string[] {
    const messages = this.validateForeignKeyForm(value);

    if (this.validateMappings() && this.message) {
      messages.push(this.message);
    }

    const duplicateFkMessage = this.checkDuplicateForeignKey(value, mappings);
    if (duplicateFkMessage) {
      messages.push(duplicateFkMessage);
    }

    return messages;
  }

  private buildForeignKeyData(mappings: { parent: string; child: string }[]): any {
    return {
      ...this.foreignKeyForm.value,
      mappings: mappings,
      parentIndexName: this.indexData.indexName,
      indexRefAppId: null
    };
  }

  private resolveIndexRefAppId(parentTableSid: string): string | null {
    if (this.applicationType === 'INDEPENDENT') {
      return this.appGlobalService.get('appId') as string;
    }
    const filteredApps = (this.groupedLookUpTables as { items: { sid: string }[]; sid: string }[]).filter(
      (app) => Array.isArray(app.items) && app.items.some((tbl) => tbl.sid === parentTableSid)
    );
    return filteredApps.length > 0 ? filteredApps[0].sid : null;
  }

  private executeSaveAction(data: any): void {
    const indexRefAppId = this.resolveIndexRefAppId(data.parentTableSid);
    const saveData = { ...data, sid: uuidv4(), indexRefAppId };
    this.isLoading = true;
    this.set(saveData);
    this.indexNameMap = null;
  }

  private hasNoChanges(data: any): boolean {
    return (
      data?.parentTableSid === this.backupForeignKey?.parentTableSid &&
      data?.parentIndexId === this.backupForeignKey?.parentIndexId &&
      isEqual(data?.mappings, this.backupForeignKey?.mappings) &&
      data?.fkName?.toLowerCase() === this.backupForeignKey?.fkName?.toLowerCase()
    );
  }

  private executeUpdateAction(data: any): void {
    if (this.hasNoChanges(data)) {
      this.showMessage('warn', this.translate.instant('NO_CHANGE'));
      return;
    }
    const actionType = this.determineForeignKeyUpdateActionType(data, this.backupForeignKey);
    this.loader.show();
    this.referenceUtil
      .handleWithReferences({
        payload: {
          modelId: data?.sid,
          modelType: ModelType.FOREIGN_KEY,
          actionType: actionType,
          action: UiActions.UPDATE
        },
        node: { name: data?.fkName, type: 'Foreign Key' },
        onConfirm: () => this.updateFk(data),
        onNoReferences: () => this.updateFk(data),
        onError: () => this.updateFk(data)
      })
      .catch(() => this.updateFk(data))
      .finally(() => this.loader.hide());
  }

  private validateForeignKeyForm(value: any): string[] {
    const messages: string[] = [];

    if (!value.fkName || !value.parentTableSid || !value.parentIndexId) {
      messages.push(this.translate.instant('MANDATORY_FIELDS'));
    }

    if (!this.foreignKeyForm.get('fkName').value) {
      return messages;
    }

    const nameValidationError = this.validateForeignKeyName(value);
    if (nameValidationError) {
      messages.push(nameValidationError);
    }

    return messages;
  }

  private validateForeignKeyName(value: any): string | null {
    const fkNameControl = this.foreignKeyForm.get('fkName');
    const name = fkNameControl.value?.trim().replace(/\s\s+/g, ' ');
    fkNameControl.patchValue(name);

    // Check if name matches a field name
    const fieldIndex = this.allFields.findIndex((fields) => this.trimName(fields.name) === this.trimName(value.fkName));
    if (fieldIndex > -1) {
      fkNameControl.setErrors({ fldname: true });
      return this.translate.instant('FIELD_AS_FOREIGNKEY');
    }

    // Check for duplicate name
    if (this.isDuplicateName(value)) {
      fkNameControl.setErrors({ duplicate: true });
      return this.translate.instant('DUPLICATE_FK_NAME');
    }

    // Check for pattern error
    if (fkNameControl.hasError('pattern')) {
      return this.translate.instant('INVALID_FKNAME');
    }

    // Check for reserved keywords
    if (this.util.keywords.includes(name.toLowerCase()) || this.util.sqlKeywords.includes(name.toLowerCase())) {
      fkNameControl.setErrors({ keyword: true });
      return this.translate.instant('KEYWORD_FOREIGNKEY');
    }

    return null;
  }

  private checkDuplicateForeignKey(value: any, mappings: any[]): string | null {
    const isDuplicate = this.foreignKeys.find(
      (fk) =>
        fk.parentTableSid === value.parentTableSid &&
        fk.sid !== value.sid &&
        fk.parentIndexId === value.parentIndexId &&
        isEqual(fk.mappings, mappings)
    );

    return isDuplicate ? this.translate.instant('DUPLICATE_FOREIGN_KEY') : null;
  }

  updateFk(data: any) {
    if (this.applicationType != ApplicationTypes.INDEPENDENT) {
      data = {
        ...data,
        ...{
          indexRefAppId: this.groupedLookUpTables.filter(
            (app) => app?.items?.filter((tbl) => tbl.sid == data.parentTableSid).length > 0
          )[0]?.sid
        }
      };
    } else {
      data = { ...data, indexRefAppId: this.appGlobalService.get('appId') };
    }
    this.isLoading = true;
    this.set(data, true);
    this.indexNameMap = null;
  }

  closeDialog() {
    this.showAddForm = false;
    this.action = '';
    this.mappings = [];
    this.foreignKeyForm.reset();
    this.indexData = null;
    this.parentTblFlds = [];
    this.indexList = [];
    this.submitted = false;
  }

  compareAllowedVals(parFld, childFld) {
    if (parFld.f_type.toLowerCase() === 'dropdown' || parFld.f_type.toLowerCase() === 'radio') {
      if (parFld.f_aValues && childFld.f_aValues) {
        try {
          const val1 = JSON.parse(parFld.f_aValues).values;
          const val2 = JSON.parse(childFld.f_aValues).values;
          if (val1.length !== val2.length) {
            return false;
          }
          const elements = new Set([...val1, ...val2]);
          for (const x of elements) {
            const count1 = val1.filter((e) => e?.name?.toLowerCase() === x?.name?.toLowerCase()).length;
            const count2 = val2.filter((e) => e?.name?.toLowerCase() === x?.name?.toLowerCase()).length;
            if (count1 !== count2) {
              return false;
            }
          }
        } catch (e) {
          console.error(e);
        }
      } else if ((parFld.f_aValues && !childFld.f_aValues) || (!parFld.f_aValues && childFld.f_aValues)) {
        return false;
      }
    }
    return true;
  }

  onchildFldCng(event, data) {
    data.errorMsg = null;
    data.errorType = null;
    const parentTblId = this.parentTblFlds?.[0]?.f_tableId;
    const checkMandatoryField =
      event?.sid &&
      this.allFields.find((f) => f.sid === event.sid && parentTblId === f?.f_tableId)?.f_mandatory === 'yes';
    if (checkMandatoryField) {
      this.confirmationDialogService.confirm({
        message: this.translate.instant('FOREIGN_KEY_UPDATE_MESSAGE'),
        header: 'Confirmation',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => {
          this.oldMappings = cloneDeep(this.mappings);
          this.updateMandatoryProp(event);
        },
        reject: () => {
          const oldValue = this.oldMappings.find((m) => m?.parent === data?.parent)?.child;
          data.child = oldValue;
        }
      });
    } else {
      this.proceedToChange(event, data);
    }
  }

  updateMandatoryProp(event) {
    const data = [
      {
        tabId: this.appGlobalService.get('tabId'),
        aid: this.appGlobalService.get('appId'),
        sid: event.sid,
        f_mandatory: 'no',
        type: 'FIELD',
        orgId: this.appGlobalService.get('orgId'),
        f_tableId: this.formData?.sid
      }
    ];
    this.bs.put(ApiConstants.updateFields, data).subscribe({
      next: (data: any) => {
        /* empty */
      }
    });
  }

  proceedToChange(event, data) {
    let message = '';
    const parFld = this.parentTblFlds.find((fld) => fld.sid === data.parent);
    const childFld = this.childtableFields.find((fld) => fld.sid === event.value.sid) || {};
    if (this.fieldIdMap[data.parent] === 'Sid' && this.fieldIdMap[event.sid] === 'Sid') {
      data.error = `Sid cannot be mapped to sid`;
      message = data.error;
    } else if (!this.compareAllowedVals(parFld, childFld)) {
      data.error = 'Both Fields should have same allowed values';
      message = data.error;
    } else {
      delete data.error;
    }
    if (message) {
      this.showMessage('error', message);
    }
    const items = this.mappings.map((obj) => {
      return obj.parent === data.parent ? { ...obj, ...data } : obj;
    });
    this.mappings = [...items];
    const oldMapping = this.oldMappings.find((m) => m?.parent === data?.parent);
    if (oldMapping) {
      oldMapping.child = data.child;
    }
  }

  deleteForeignKey(data) {
    this.store.dispatch(deleteFKFailure({ fkDelFailure: null }));
    this.store.dispatch(deleteFKSuccess({ fkDelSuccess: null }));
    this.loader.show();
    this.referenceUtil
      .handleWithReferences({
        payload: {
          modelId: data?.sid,
          modelType: ModelType.FOREIGN_KEY,
          actionType: ActionType.FOREIGN_KEY_DELETE,
          action: UiActions.DELETE
        },
        node: { name: data?.fkName, type: 'Foreign Key' },
        onConfirm: () => this.confirmDeletion(data.sid, data.fkName),
        onNoReferences: () => this.confirmDeletion(data.sid, data.fkName),
        onError: () => this.confirmDeletion(data.sid, data.fkName)
      })
      .catch(() => this.confirmDeletion(data.sid, data.fkName))
      .finally(() => this.loader.hide());
  }

  confirmDeletion(sid, fkName, value?: any, fromReferencePopup?: boolean) {
    const message = this.formConfirmationMessage(fkName, value);
    let confirmationObj;

    if (!fromReferencePopup) {
      confirmationObj = {
        message,
        header: 'Confirmation',
        accept: () => {
          this.confirmDelForeignKey(sid); // 'this' works correctly with arrow functions
        },
        reject: () => {
          console.log('Deletion cancelled');
        }
      };
      this.zone.run(() => {
        this.confirmationDialogService.confirm(confirmationObj);
      });
    } else {
      this.confirmDelForeignKey(sid);
    }
  }

  confirmDelForeignKey(sid) {
    this.loader.show();
    this.store.dispatch(
      deleteForeignKey({
        delApiConstants: { ...ApiConstants.deleteForeignKey },
        data: { appId: this.appId, orgId: this.orgId, tableId: this.formData?.sid, foreignKeyId: sid }
      })
    );
  }

  formConfirmationMessage(fkName, value) {
    let message = '';
    message += this.translate.instant('FOREIGN_KEY_DELETION_CONFIRMATION', { fkName: fkName });
    return message;
  }

  toggleGroup(event: Event, group: any) {
    event.stopPropagation();
    event.preventDefault();
    const groupItem = this.groupedLookUpTables.find((ci) => ci.label === group['label']);
    if (groupItem) {
      groupItem.expanded = !groupItem.expanded;
      const isExpanded = groupItem.expanded;
      if (groupItem.items) {
        groupItem.items.forEach((fld) => {
          fld.expanded = isExpanded;
        });
      }
    }
    // Force Angular to update the view first
    this.groupedLookUpTables = [...this.groupedLookUpTables];
    this.cdr.detectChanges();
    // Use requestAnimationFrame to ensure DOM is updated before applying jQuery
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.updateOptionVisibility();
      }, 0);
    });
  }

  updateOptionVisibility() {
    $('.p-select-option').each(function () {
      if ($(this).find('.expanded').length > 0) {
        $(this).addClass('d-none');
      } else {
        $(this).removeClass('d-none');
      }
    });
  }

  preventCollapse() {
    this.groupedLookUpTables.forEach((tbl) => {
      tbl.expanded = false;
      if (tbl.items) {
        tbl.items.forEach((i) => {
          i.expanded = false;
        });
      }
    });
    this.groupedLookUpTables = [...this.groupedLookUpTables];
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.updateOptionVisibility();
    });
  }

  /**
   * Processes a single lookup table and adds/updates it in the grouped lookup tables.
   * Extracted to reduce complexity in ngOnInit.
   */
  private processLookUpTable(
    table: any,
    groupedTables: any[],
    refAppMetaInfos: Record<string, string>,
    appMetaInfos: Record<string, string>
  ): void {
    if (!this.hasTableMeta(table.sid, refAppMetaInfos, appMetaInfos)) {
      return;
    }

    const appLabel = this.getMetaLabel(table.aid, refAppMetaInfos, appMetaInfos);
    const tableLabel = this.getMetaLabel(table.sid, refAppMetaInfos, appMetaInfos);

    const appIndex = this.findAppGroupIndex(groupedTables, table.aid);
    const tableRef = this.findTableInGroups(groupedTables, table.sid);

    if (appIndex === -1) {
      this.addAppGroupWithTable(groupedTables, table.aid, appLabel, table.sid, tableLabel);
      return;
    }

    if (!tableRef) {
      this.addTableToAppGroup(groupedTables, appIndex, table.sid, tableLabel);
      return;
    }

    this.updateTableNameIfChanged(tableRef.table, table.name, tableLabel);
  }

  private hasTableMeta(
    tableSid: string,
    refAppMetaInfos: Record<string, string>,
    appMetaInfos: Record<string, string>
  ): boolean {
    return !!(refAppMetaInfos[tableSid] || appMetaInfos[tableSid]);
  }

  private getMetaLabel(
    sid: string,
    refAppMetaInfos: Record<string, string>,
    appMetaInfos: Record<string, string>
  ): string {
    return refAppMetaInfos[sid] ? refAppMetaInfos[sid] : appMetaInfos[sid];
  }

  private findAppGroupIndex(groupedTables: any[], appId: string): number {
    return groupedTables.findIndex((tb) => tb.sid === appId);
  }

  private findTableInGroups(groupedTables: any[], tableSid: string): { group: any; table: any } | null {
    for (const group of groupedTables) {
      const table = group.items?.find((tbl) => tbl.sid === tableSid);
      if (table) {
        return { group, table };
      }
    }
    return null;
  }

  private addAppGroupWithTable(
    groupedTables: any[],
    appId: string,
    appLabel: string,
    tableSid: string,
    tableLabel: string
  ): void {
    groupedTables.push({
      expanded: false,
      label: appLabel,
      sid: appId,
      items: [
        {
          name: tableLabel,
          sid: tableSid,
          expanded: false
        }
      ]
    });
    this.tableMap[tableSid] = tableLabel;
  }

  private addTableToAppGroup(groupedTables: any[], appIndex: number, tableSid: string, tableLabel: string): void {
    const group = groupedTables[appIndex];
    if (!group?.items) {
      return;
    }
    group.items.push({
      name: tableLabel,
      sid: tableSid,
      expanded: false
    });
    this.tableMap[tableSid] = tableLabel;
  }

  private updateTableNameIfChanged(existingTable: any, currentName: string, tableLabel: string): void {
    if (existingTable && existingTable.name !== currentName) {
      existingTable.name = tableLabel;
      this.tableMap[existingTable.sid] = tableLabel;
    }
  }

  ngOnInit(): void {
    this.appId = this.appGlobalService.get('appId');
    this.isLoading = true;
    this.appId = this.appGlobalService.get('appId');
    this.orgId = this.appGlobalService.get('orgId');
    this.fieldMap = { ...this.fieldIdMap };
    this.tableMap = { ...this.tableIdMap };
    const metainfoSub = this.store.select(metaInfo).subscribe((appmetaInfo: any) => {
      if (appmetaInfo) {
        this.metaInfo = cloneDeep(appmetaInfo);
      }
    });
    this.addToSubscriptionsList(metainfoSub);
    if (this.applicationType !== ApplicationTypes.INDEPENDENT) {
      this.refMetaInfo = this.appGlobalService.get('refAppMetaInfos') || {};
      const lookUpSub = this.store.select(lookUpTables).subscribe((lookUpTable: any) => {
        if (lookUpTable) {
          this.isLoading = false;
          let cloneGroupedLookUpTables = cloneDeep(this.groupedLookUpTables);
          this.groupedLookUpTables = [];
          const refAppMetaInfos = this.appGlobalService.get('refAppMetaInfos') || {};
          const appMetaInfos = this.appGlobalService.get('appmetaInfo') || {};
          lookUpTable.forEach((table) => {
            this.processLookUpTable(table, cloneGroupedLookUpTables, refAppMetaInfos, appMetaInfos);
          });
          this.groupedLookUpTables = [...cloneGroupedLookUpTables];
          this.sortGroupedLookUpTables();

          // Fix: PrimeNG p-select caches options internally and doesn't detect changes
          // when the store emits data asynchronously (first emit is undefined, second has data).
          // Solution: Reset array to empty, trigger change detection, then reassign in next tick.
          // This forces p-select to fully reinitialize its options.
          const updatedTables = [...this.groupedLookUpTables];
          this.groupedLookUpTables = [];
          this.cdr.detectChanges();
          setTimeout(() => {
            this.groupedLookUpTables = updatedTables;
            this.appGlobalService.write('groupedLookUpTables', this.groupedLookUpTables);
            this.cdr.detectChanges();
          });
        }
      });
      this.addToSubscriptionsList(lookUpSub);
    }
    let fldSub = this.store.select(tableFieldsForDM).subscribe((res: any) => {
      if (res) {
        this.allFields = [];
        if (res.length > 0) {
          this.allFields = [...res];
          this.childtableFields = res.filter(
            (fld) =>
              fld.f_type !== 'Image Carousel' &&
              fld.f_type !== 'File Attachments' &&
              fld.f_type !== 'Complex Datatype' &&
              !fld.f_transientField
          );
          this.allFields$.next(this.allFields);
          // && (!this.currentTblindices.find(ind => ind.indexType === IndexType.PRIMARY && ind.fields.includes(fld.sid)))));
        }
      }
    });
    this.addToSubscriptionsList(fldSub);
    const tabSub = this.store.select(allTables).subscribe((res: any) => {
      if (res) {
        if (res.length > 0) {
          this.tableList = [...res];
        }
      }
    });
    this.addToSubscriptionsList(tabSub);
    const saveFailureSub = this.store.select(fKSaveFailure).subscribe((err: any) => {
      if (err) {
        this.isLoading = false;
        this.loader.hide();
        this.util.showErrMsg({
          apiErr: err,
          tableMap: this.tableMap,
          fieldMap: this.fieldIdMap,
          admData: this.formData
        });
      }
    });
    this.subscriptions.push(saveFailureSub);
    const deleteSuccess = this.store.select(fkDelSuccess).subscribe((foreignKeyId: any) => {
      if (foreignKeyId) {
        this.loader.hide();
        this.foreignKeys = this.foreignKeys.filter((fk) => fk.sid !== foreignKeyId);
      }
    });
    this.subscriptions.push(deleteSuccess);

    const deleteFailure = this.store.select(fkDelFailure).subscribe((err: any) => {
      if (err) {
        this.loader.hide();
      }
    });
    this.subscriptions.push(deleteFailure);
    const allIndicesSub = this.store.select(allIndices).subscribe((allIndices: any) => {
      if (allIndices && allIndices.length > 0) {
        this.currentTblindices = [...allIndices];
      } else {
        this.currentTblindices = null;
      }
    });

    this.subscriptions.push(allIndicesSub);
    if (this.applicationType !== ApplicationTypes.INDEPENDENT) {
      this.store.dispatch(getLookUpTables({ apiConstants: ApiConstants.getLookUpTables }));
    }

    this.getDataFromFS();
  }
  /**
   * Sorts the grouped lookup tables in the dropdown options.
   */
  sortGroupedLookUpTables() {
    this.groupedLookUpTables.forEach((l) => {
      this.sortByField(l?.items, 'name');
    });
  }
  /**
   * Sorts the grouped lookup tables by each array with corresponding field to be sorted.
   *
   * @param {array} arr - Array to be sorted.
   * @param {string} field - Array's corresponding field which has to be sorted.
   * @returns {number} - Returns comparision order in which array elements get sorted with corresponding field.
   */
  sortByField = (arr, field) => {
    return arr.sort((a, b) => {
      if (a[field] < b[field]) {
        return -1;
      } else if (a[field] > b[field]) {
        return 1;
      }

      return 0;
    });
  };
  ngOnDestroy(): void {
    this.store.dispatch(deleteFKFailure({ fkDelFailure: null }));
    this.store.dispatch(deleteFKSuccess({ fkDelSuccess: null }));
    this.store.dispatch(updateFKFailure({ fKSaveFailure: null }));
    this.store.dispatch(setIndices({ indices: null }));
    this.unsubscribeListener();
  }

  showWhereUsed(data): void {
    this.isLoading = true;
    this.referenceUtil.showWhereUsed(
      {
        id: data.sid,
        name: data.fkName,
        type: this.translate.instant('FOREIGN_KEY')
      },
      { hideLoader: () => (this.isLoading = false) }
    );
  }

  getDataFromFS() {
    let version = this.appGlobalService.get('appVersion');
    const queryObject = {
      collectionName: `ForeignKey_${version}`,
      filter: [
        {
          field: 'appId',
          operator: '==',
          value: this.appId
        },
        {
          field: 'tableId',
          operator: '==',
          value: this.formData.sid
        }
      ]
    };
    this.isLoading = true;
    this.getForeignKeysData(queryObject);
  }

  getForeignKeysData(queryObject: any) {
    try {
      if (this.fsDataSub && typeof this.fsDataSub?.unsubscribe === 'function') {
        this.fsDataSub.unsubscribe();
      }

      this.fsDataSub = this.fsService.fireService(queryObject).subscribe(
        (res: any) => {
          if (res && res.length > 0) {
            let data = [];

            const extractForeignKeyFields = (d: any) => ({
              sid: d.sid,
              fkName: d.fkName,
              parentTableSid: d.parentTableSid,
              parentIndexId: d.parentIndexId,
              indexRefAppId: d.indexRefAppId,
              mappings: d.mappings,
              parentChildTableFk: d.parentChildTableFk || false,
              appId: d.appId,
              tableId: d.tableId,
              orgId: d.orgId || this.orgId
            });
            this.isLoading = false;
            this.loader.hide();
            this.closeDialog();

            if (this.initialCall) {
              this.initialCall = false;
              const nonCachedRes = res?.filter((dt) => !dt.payload.doc.metadata.fromCache) || [];
              // allForeignKeys includes all FKs (including deleted) for name generation
              this.allForeignKeys = nonCachedRes.map((obj) => extractForeignKeyFields(obj.payload.doc.data()));
              // foreignKeys only contains non-deleted FKs for display
              const filteredRes = nonCachedRes.filter((dt) => !dt.payload.doc.data().recDeleted);
              data = filteredRes.map((obj) => extractForeignKeyFields(obj.payload.doc.data()));
              this.foreignKeys = data;
            } else {
              res.forEach((dt) => {
                const docData = dt.payload.doc.data();
                const sid = docData.sid;
                const index = this.foreignKeys.findIndex((idx) => idx.sid === sid);
                const allIndex = this.allForeignKeys.findIndex((idx) => idx.sid === sid);

                // Always add/update in allForeignKeys (for name generation)
                const newFk = extractForeignKeyFields(docData);
                if (allIndex !== -1) {
                  this.allForeignKeys[allIndex] = { ...this.allForeignKeys[allIndex], ...newFk };
                } else {
                  this.allForeignKeys.push(newFk);
                }

                if (docData.recDeleted) {
                  // Remove from Foreign Keys if recDeleted is true (display list only)
                  if (index !== -1) {
                    this.foreignKeys.splice(index, 1);
                  }
                } else if (dt.payload.doc.exists || dt.type === 'added' || dt.type === 'modified') {
                  // Add or update Foreign Key in foreignKeys (display list)
                  if (index !== -1) {
                    // Update existing Foreign Keys
                    this.foreignKeys[index] = { ...this.foreignKeys[index], ...newFk };
                  } else {
                    // Add new Foreign Key
                    this.foreignKeys.push(newFk);
                  }
                }
              });
            }
          }
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          console.error(error);
        }
      );
      this.subscriptions.push(this.fsDataSub);
    } catch (e) {
      this.isLoading = false;
    }
  }

  unsubscribeListener() {
    this.subscriptions.forEach((k) => {
      if (k && typeof k.unsubscribe == 'function') {
        k.unsubscribe();
      }
    });
  }

  private determineForeignKeyUpdateActionType(newData: any, oldData: any): string {
    try {
      if (!oldData) return ActionType.FOREIGN_KEY_FIELD_UPDATE;
      const transientParent = (newData?.parentTableFields || []).filter((f) => f?.f_transientField).map((f) => f.sid);
      const transientChild = (newData?.childTableFields || []).filter((f) => f?.f_transientField).map((f) => f.sid);
      if (
        (newData?.mappings || []).some((m) => transientParent.includes(m.parent) || transientChild.includes(m.child))
      ) {
        return ActionType.TRANSIENT_FIELD_IN_FOREIGN_KEY;
      }
      if (newData.parentIndexId !== oldData.parentIndexId) return ActionType.FOREIGN_KEY_INDEX_CHANGE;
      const oldMap = (oldData.mappings || []).map((m) => ({ parent: m.parent, child: m.child }));
      const newMap = (newData.mappings || []).map((m) => ({ parent: m.parent, child: m.child }));
      const oldParents = new Set(oldMap.map((m) => m.parent));
      const newParents = new Set(newMap.map((m) => m.parent));
      for (const p of oldParents) if (!newParents.has(p)) return ActionType.FOREIGN_KEY_FIELD_UPDATE;
      const oldChildren = new Set(oldMap.map((m) => m.child));
      const newChildren = new Set(newMap.map((m) => m.child));
      for (const c of oldChildren) if (!newChildren.has(c)) return ActionType.FOREIGN_KEY_FIELD_UPDATE;
      const changed =
        oldMap.length !== newMap.length ||
        oldMap.some((o) => !newMap.find((n) => n.parent === o.parent && n.child === o.child));
      if (changed) return ActionType.FOREIGN_KEY_FIELD_UPDATE;
      if (newData.fkName?.toLowerCase() !== oldData.fkName?.toLowerCase()) return ActionType.FOREIGN_KEY_RENAME;
      return ActionType.FOREIGN_KEY_FIELD_UPDATE;
    } catch {
      return ActionType.FOREIGN_KEY_FIELD_UPDATE;
    }
  }
}


